/**
 * Mapeo de roles a permisos para el sistema Stand Virtual
 * Define los permisos específicos para cada rol según las especificaciones del negocio
 */

export const ROLES = {
  MASTER: 'master',
  ADMIN: 'admin',
  ADMINISTRADOR: 'administrador', 
  MONITOR: 'monitor'
};

export const PERMISSIONS = {
  // Administración de Usuarios
  ADMIN_MANAGE_USERS: 'admin:manage_users',
  USER_VIEW: 'user:view',
  USER_SEARCH: 'user:search',
  USER_CREATE: 'user:create',
  USER_UPDATE: 'user:update',
  USER_DELETE: 'user:delete',
  ROLE_ASSIGN: 'role:assign',
  
  // Configuración Stand Virtual
  CONFIG_STAND_VIRTUAL: 'config:stand_virtual',
  CONFIG_STAND_VIRTUAL_VIEW: 'config:stand_virtual:view',
  CONFIG_STAND_VIRTUAL_EDIT: 'config:stand_virtual:edit',
  CONFIG_PSS_ENABLE: 'config:pss_enable',
  CONFIG_PROCEDURES: 'config:procedures',
  CONFIG_COVERAGES: 'config:coverages',
  CONFIG_PSS_TYPES: 'config:pss_types',
  CONFIG_ACTIVATIONS: 'config:activations',
  
  // Reportería
  REPORT_VIEW: 'report:view',
  REPORT_EXPORT: 'report:export',
  REPORT_EXCEL: 'report:excel',
  REPORT_PDF: 'report:pdf',
  METRICS_VIEW: 'metrics:view',
  
  // Configuración de Documentos/Procedimientos
  CONFIG_DOCUMENTS: 'config:documents',
  CONFIG_DOCUMENTS_VIEW: 'config:documents:view',
  CONFIG_DOCUMENTS_EDIT: 'config:documents:edit'
};

/**
 * Mapeo de roles a sus permisos correspondientes
 */
export const ROLE_PERMISSIONS_MAP = {
  [ROLES.MASTER]: [
    // Administración de Usuarios - COMPLETO
    PERMISSIONS.ADMIN_MANAGE_USERS,
    PERMISSIONS.USER_VIEW,
    PERMISSIONS.USER_SEARCH,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_UPDATE,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.ROLE_ASSIGN,
    
    // Configuración Stand Virtual - COMPLETO
    PERMISSIONS.CONFIG_STAND_VIRTUAL,
    PERMISSIONS.CONFIG_STAND_VIRTUAL_VIEW,
    PERMISSIONS.CONFIG_STAND_VIRTUAL_EDIT,
    PERMISSIONS.CONFIG_PSS_ENABLE,
    PERMISSIONS.CONFIG_PROCEDURES,
    PERMISSIONS.CONFIG_COVERAGES,
    PERMISSIONS.CONFIG_PSS_TYPES,
    PERMISSIONS.CONFIG_ACTIVATIONS,
    
    // Reportería - COMPLETO
    PERMISSIONS.REPORT_VIEW,
    PERMISSIONS.REPORT_EXPORT,
    PERMISSIONS.REPORT_EXCEL,
    PERMISSIONS.REPORT_PDF,
    PERMISSIONS.METRICS_VIEW,
    
    // Configuración de Documentos - COMPLETO
    PERMISSIONS.CONFIG_DOCUMENTS,
    PERMISSIONS.CONFIG_DOCUMENTS_VIEW,
    PERMISSIONS.CONFIG_DOCUMENTS_EDIT
  ],
  
  [ROLES.ADMIN]: [
    // NO tiene administración de usuarios
    
    // Configuración Stand Virtual - COMPLETO
    PERMISSIONS.CONFIG_STAND_VIRTUAL,
    PERMISSIONS.CONFIG_STAND_VIRTUAL_VIEW,
    PERMISSIONS.CONFIG_STAND_VIRTUAL_EDIT,
    PERMISSIONS.CONFIG_PSS_ENABLE,
    PERMISSIONS.CONFIG_PROCEDURES,
    PERMISSIONS.CONFIG_COVERAGES,
    PERMISSIONS.CONFIG_PSS_TYPES,
    PERMISSIONS.CONFIG_ACTIVATIONS,
    
    // Reportería - COMPLETO
    PERMISSIONS.REPORT_VIEW,
    PERMISSIONS.REPORT_EXPORT,
    PERMISSIONS.REPORT_EXCEL,
    PERMISSIONS.REPORT_PDF,
    PERMISSIONS.METRICS_VIEW,
    
    // Configuración de Documentos - COMPLETO
    PERMISSIONS.CONFIG_DOCUMENTS,
    PERMISSIONS.CONFIG_DOCUMENTS_VIEW,
    PERMISSIONS.CONFIG_DOCUMENTS_EDIT
  ],
  
  [ROLES.ADMINISTRADOR]: [
    // NO tiene administración de usuarios
    
    // Configuración Stand Virtual - COMPLETO
    PERMISSIONS.CONFIG_STAND_VIRTUAL,
    PERMISSIONS.CONFIG_STAND_VIRTUAL_VIEW,
    PERMISSIONS.CONFIG_STAND_VIRTUAL_EDIT,
    PERMISSIONS.CONFIG_PSS_ENABLE,
    PERMISSIONS.CONFIG_PROCEDURES,
    PERMISSIONS.CONFIG_COVERAGES,
    PERMISSIONS.CONFIG_PSS_TYPES,
    PERMISSIONS.CONFIG_ACTIVATIONS,
    
    // Reportería - COMPLETO
    PERMISSIONS.REPORT_VIEW,
    PERMISSIONS.REPORT_EXPORT,
    PERMISSIONS.REPORT_EXCEL,
    PERMISSIONS.REPORT_PDF,
    PERMISSIONS.METRICS_VIEW,
    
    // Configuración de Documentos - COMPLETO
    PERMISSIONS.CONFIG_DOCUMENTS,
    PERMISSIONS.CONFIG_DOCUMENTS_VIEW,
    PERMISSIONS.CONFIG_DOCUMENTS_EDIT
  ],
  
  [ROLES.MONITOR]: [
    // NO tiene administración de usuarios
    
    // Configuración Stand Virtual - SOLO VISUALIZACIÓN
    PERMISSIONS.CONFIG_STAND_VIRTUAL_VIEW,
    
    // Reportería - COMPLETO
    PERMISSIONS.REPORT_VIEW,
    PERMISSIONS.REPORT_EXPORT,
    PERMISSIONS.REPORT_EXCEL,
    PERMISSIONS.REPORT_PDF,
    PERMISSIONS.METRICS_VIEW,
    
    // Configuración de Documentos - SOLO VISUALIZACIÓN
    PERMISSIONS.CONFIG_DOCUMENTS_VIEW
  ]
};

/**
 * Obtiene los permisos para un rol específico
 * @param {string} role - El rol del usuario
 * @returns {Array<string>} - Array de permisos para el rol
 */
export const getPermissionsForRole = (role) => {
  if (!role) return [];
  
  const normalizedRole = role.toLowerCase();
  return ROLE_PERMISSIONS_MAP[normalizedRole] || [];
};

/**
 * Obtiene todos los permisos para una lista de roles
 * @param {Array<string>} roles - Array de roles del usuario
 * @returns {Array<string>} - Array único de permisos
 */
export const getPermissionsForRoles = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) return [];
  
  const allPermissions = roles.reduce((acc, role) => {
    const rolePermissions = getPermissionsForRole(role);
    return [...acc, ...rolePermissions];
  }, []);
  
  // Retornar permisos únicos
  return [...new Set(allPermissions)];
};

/**
 * Verifica si un usuario tiene un rol específico
 * @param {Array<string>} userRoles - Roles del usuario
 * @param {string} targetRole - Rol a verificar
 * @returns {boolean} - true si el usuario tiene el rol
 */
export const hasRole = (userRoles, targetRole) => {
  if (!Array.isArray(userRoles) || !targetRole) return false;
  
  return userRoles.some(role => {
    const normalizedRole = role.toLowerCase();
    const normalizedTarget = targetRole.toLowerCase();
    
    // Admitir tanto 'admin' como 'administrador'
    if ((normalizedRole === 'admin' || normalizedRole === 'administrador') && 
        (normalizedTarget === 'admin' || normalizedTarget === 'administrador')) {
      return true;
    }
    
    return normalizedRole === normalizedTarget;
  });
};

/**
 * Obtiene el rol de mayor jerarquía del usuario
 * @param {Array<string>} userRoles - Roles del usuario
 * @returns {string|null} - El rol de mayor jerarquía o null
 */
export const getHighestRole = (userRoles) => {
  if (!Array.isArray(userRoles) || userRoles.length === 0) return null;
  
  const roleHierarchy = [ROLES.MASTER, ROLES.ADMIN, ROLES.ADMINISTRADOR, ROLES.MONITOR];
  
  for (const role of roleHierarchy) {
    if (hasRole(userRoles, role)) {
      return role;
    }
  }
  
  return null;
};

/**
 * Verifica si un usuario puede acceder a una funcionalidad específica
 * @param {Array<string>} userRoles - Roles del usuario
 * @param {Array<string>} requiredPermissions - Permisos requeridos
 * @returns {boolean} - true si el usuario puede acceder
 */
export const canAccessFeature = (userRoles, requiredPermissions) => {
  if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) return true;
  if (!Array.isArray(userRoles) || userRoles.length === 0) return false;
  
  const userPermissions = getPermissionsForRoles(userRoles);
  
  return requiredPermissions.some(permission => 
    userPermissions.includes(permission)
  );
};

const RolePermissionsMapping = {
  ROLES,
  PERMISSIONS,
  ROLE_PERMISSIONS_MAP,
  getPermissionsForRole,
  getPermissionsForRoles,
  hasRole,
  getHighestRole,
  canAccessFeature
};

export default RolePermissionsMapping;
